import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Modal } from "reactstrap";
import DataTablePageQuery from "../../../components/DataTablePageQuery";
import { fetchTags } from "../../tags/data/tagService";
import { fetchImportTemplateByFilter, deleteImportTemplate } from "../data/importTemplateService";
import AdminImportTemplateModal from "./AdminImportTemplateModal";
import { formatDateTime } from "../../../utils/dateUtils";
import { createAudit } from '../../../features/audit/data/auditService';
import TagsList from "../../tags/components/TagsList";
import UserImportTemplateModal from "./UserImportTemplateModal";
import { useLockState } from "../../../components/userLocks/LockCheck";

const AdminImportTemplateTable = ({ user = false, addQuoteItems }) => {
    // general state
    const [modal, setModal] = useState(false);
    const [downloadButtonIsEnabled, setDownloadButtonIsEnabled] = useState(true);
    const searchableFields = ["name", "description", "tags", "createdAt", "updatedAt", "disabled"]
    const lockState = useLockState();

    // template state
    const [importTemplates, setImportTemplates] = useState([]);
    const [selectedImportTemplate, setSelectedImportTemplate] = useState(null);

    // pagination state
    const [filter, setFilter] = useState({ pageNumber: 1, pageSize: 10, disabled: user ? true : false });
    const [totalRecords, setTotalRecords] = useState(0);

    // Fetch Tags using useQuery
    const {
        data: tagsData,
        isLoading: isLoadingTags,
        error: tagsError,
    } = useQuery({
        queryKey: ["tagsFilter"],
        queryFn: fetchTags,
        staleTime: 60000
    });

    const getImportTemplates = async () => {
        const importTemplates = await fetchImportTemplateByFilter(filter);
        setTotalRecords(importTemplates.totalRecords);
        if (importTemplates.totalRecords === 0) return []

        return importTemplates.importTemplateItems.map(({ id, name, description, importTemplateTags, s3Key, configJson, disabled, createdAt, updatedAt }) => {
            return {
                id,
                name,
                description,
                tags: importTemplateTags.length > 0
                    ? importTemplateTags
                        .map(tag => tagsData.find(w => w.id === tag.tagId))
                        .filter(f => f)
                    : [],
                s3Key,
                configJson: JSON.parse(configJson),
                disabled,
                createdAt,
                updatedAt
            };
        });
    }

    // Fetch data using useQuery
    const {
        data: importTemplateData,
        isLoading: isLoadingImportTemplateData,
        error: importTemplateDataError,
        refetch: refetchImportTemplates,
    } = useQuery({
        queryKey: ["importTemplates", user ? "User" : "Admin"],
        queryFn: getImportTemplates,
        enabled: !!tagsData,
        staleTime: 60000
    });

    const tagsOptions = tagsData || [];

    // this query will run when the user presses the 'export' button
    // it will be passed as a prop to the Download component and will fetch all records that meet the filter criteria, not just the current page records
    const getImportTemplateQueryForDownload = async () => {
        let downloadFilter = { ...filter }
        delete downloadFilter.pageSize
        delete downloadFilter.pageNumber

        setDownloadButtonIsEnabled(false);

        const importTemplates = await fetchImportTemplateByFilter(downloadFilter);

        setDownloadButtonIsEnabled(true);

        return importTemplates.importTemplateItems.map(({ id, name, description, importTemplateTags, s3Key, configJson, disabled, createdAt, updatedAt }) => {
            return {
                id,
                name,
                description,
                tags: importTemplateTags.length > 0
                    ? importTemplateTags
                        .map(t => tagsData.find(w => w.id === t.tagId))
                        .filter(tag => tag && tag.tagName)
                        .map(tag => tag.tagName)
                        .join(", ")
                    : "",
                s3Key,
                configJson: JSON.parse(configJson),
                disabled,
                createdAt: createdAt ? formatDateTime(createdAt) : null,
                updatedAt: updatedAt ? formatDateTime(updatedAt) : null
            };
        });
    }

    // When data is fetched, update our local state.
    useEffect(() => {
        if (importTemplateData) {
            setImportTemplates(importTemplateData);
        }
    }, [importTemplateData]);

    useEffect(() => {
        if (tagsData) {
            refetchImportTemplates();
        }
    }, [filter, tagsData]);

    const changeFilter = (obj) => {
        const filterId = obj.filterId
        const value = obj.value

        if (filterId === "pageNumber") {
            setFilter(prev => ({ ...prev, [filterId]: value }));
        } else if (filterId === "tags") {
            if (value === undefined) {
                setFilter(({ importTemplateTagId, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                setFilter(prev => ({ ...prev, importTemplateTagId: value, pageNumber: 1 }));
            }
        } else if (filterId === "searchTerm") {
            if (value === '') {
                setFilter(({ searchOptions, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                let searchOptions = { searchableFields: searchableFields, searchTerm: value, fieldSearchTerms: [] }

                // try to find tags in search parameter
                // if found, add the tagIds to the searchOptions
                if (searchableFields.find(w => w === "tags") && tagsData) {
                    const tagIds = tagsData.filter(w => w.tagName.toLowerCase().includes(value.toLowerCase())).map(u => u.id.toString());
                    if (tagIds.length > 0) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "tags", "values": tagIds })
                    }
                }

                // try to find the words 'true' and 'false' in search parameter
                // if found, add 'disabled' to the searchOptions
                if (searchableFields.find(w => w === "disabled")) {
                    const disabledPossibleValues = ["y", "yes"];
                    const enabledPossibleValues = ["n", "no"];
                    if (disabledPossibleValues.includes(value.trim().toLowerCase())) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "disabled", "values": ["true"] })
                    } else if (enabledPossibleValues.includes(value.trim().toLowerCase())) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "disabled", "values": ["false"] })
                    }
                }

                setFilter(prev => ({ ...prev, searchOptions: searchOptions, pageNumber: 1 }));
            }
        } else {
            setFilter(prev => ({ ...prev, [filterId]: value, pageNumber: 1 }));
        }
    }

    // Handle row click to open modal with details
    const handleRowClick = (item) => {
        setSelectedImportTemplate(item);
        setModal(true);         // Open modal
    };

    const onToggle = () => {
        setImportTemplates((prevImportTemplates) => prevImportTemplates.filter(i => i.id !== 0))
        setModal(!modal)
    }

    const handleTemplateChangeFromModal = (template, isNewTemplate) => {
        setSelectedImportTemplate(template)

        if (isNewTemplate) {
            setImportTemplates([...importTemplates, template]);
        } else {
            setImportTemplates(importTemplates.map((i) => i.id === template.id ? template : i));
        }
    }

    const handleNewTemplateClick = () => {
        const newTemplate = {
            "id": 0, "name": null, "description": null, "tags": [], "file": null, "configJson": { sheets: [] }, "disabled": false
        }
        setImportTemplates([...importTemplates, newTemplate]); // Add new template to importTemplates array (newTemplate)
        setSelectedImportTemplate(newTemplate);
        setModal(true);
    }

    const columns = useMemo(() => {
        const base = [
            { key: "name", label: "Name" },
            { key: "description", label: "Description" },
            {
                key: "createdAt",
                label: "Created",
                render: (value) => <span>{value ? formatDateTime(new Date(value)) : ""}</span>,
            },
            {
                key: "updatedAt",
                label: "Updated",
                render: (value) => <span>{value ? formatDateTime(new Date(value)) : ""}</span>,
            },
            {
                key: "tags",
                label: "Tags",
                render: (value, row) => (
                    <TagsList tags={row.tags} />
                )
            },
        ];

        //hide certain columns based on user flag
        if (!user) {
            base.push({
                key: "disabled",
                label: "Disabled",
                render: (value) => <span>{value ? "Yes" : "No"}</span>,
            });
        }

        return base;
    }, [user]);

    const handleDelete = async () => {
        setImportTemplates((prevItems) => prevItems.filter(f => f.id !== selectedImportTemplate.id));
        await deleteImportTemplate(selectedImportTemplate.id);

        await createAudit({ "action": "Import template deleted", "result": "Successful", "source": `importTemplate.${selectedImportTemplate.id}`, "payload": `{\"name\": \"${selectedImportTemplate.name}\" }` })

        setSelectedImportTemplate(null);
        setModal(false);
    }

    return (
        <>
            <DataTablePageQuery
                columns={columns}
                data={importTemplates}
                queryForDownload={getImportTemplateQueryForDownload}
                downloadButtonIsEnabled={downloadButtonIsEnabled}
                csvFileName={"importTemplates.csv"}
                exportToCsv={user === false}
                totalRecords={totalRecords}
                sortConfiguration={{ key: "name", direction: "ascending" }}
                onRowClick={(row) => !lockState?.isLocked && handleRowClick(row)}
                onFilterChange={changeFilter}
                error={importTemplateDataError || tagsError}
                isLoading={isLoadingImportTemplateData || isLoadingTags}
                filters={[
                    {
                        id: "tags",
                        label: "Tag",
                        type: "select",
                        isSearchable: true,
                        options: tagsOptions.map((item) => ({
                            value: item.id,
                            label: item.tagName,
                        }))
                    }

                ]}
                actions={[
                    !user && (
                        <Button key="new-template" onClick={handleNewTemplateClick}>
                            Add New Template
                        </Button>
                    )
                ]}

            />
            {/* Modal for displaying selected template details */}
            <Modal isOpen={modal && !user} centered size="xl" fullscreen scrollable backdrop="static"
            >
                {selectedImportTemplate &&
                    <AdminImportTemplateModal
                        toggle={onToggle}
                        template={selectedImportTemplate}
                        tagsData={tagsData || []}
                        onTemplateChange={(updatedTemplate, isNewTemplate) => handleTemplateChangeFromModal(updatedTemplate, isNewTemplate)}
                        handleDelete={handleDelete}
                    />

                }
            </Modal>

            <Modal isOpen={modal && user} centered size="xl" fullscreen scrollable backdrop="static">
                <UserImportTemplateModal
                    toggle={onToggle}
                    template={selectedImportTemplate}
                    addQuoteItems={addQuoteItems}
                />
            </Modal>
        </>
    );
}
export default AdminImportTemplateTable;